import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Brand } from "@/components/Brand";
import { CheckCircle2, LayoutDashboard, CreditCard, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
};

// Idempotent by design: this page only READS the current subscription state from the
// server (source of truth). It never calls verify-payment, never creates an order or
// invoice, and never fires a Meta Purchase event — so a refresh is always safe.
export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paymentId = params.get("payment_id") || "";
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/subscription")
      .then((r) => setSub(r.data))
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async () => {
    if (!paymentId) return;
    try {
      const r = await api.get(`/billing/invoice/${paymentId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download invoice");
    }
  };

  const planName = sub?.plan || "—";
  const amount = sub?.price;
  const statusLabel = (sub?.subscription_status || "active").replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="payment-success-page">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <Brand iconSize={34} textClass="text-xl" style={JK} />
        </div>

        <div className="border border-slate-200 bg-white shadow-sm">
          <div className="p-8 text-center border-b border-slate-100">
            <div className="h-16 w-16 rounded-full bg-[#16A34A]/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-9 w-9 text-[#16A34A]" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-slate-950" style={JK} data-testid="payment-success-title">Subscription Activated</h1>
            <p className="mt-2 text-sm text-slate-600">
              Thank you for subscribing to RecoverOS. Your plan is now active and all features are unlocked.
            </p>
          </div>

          <div className="p-8">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-slate-500 py-6" data-testid="payment-success-loading">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading your subscription…
              </div>
            ) : (
              <dl className="space-y-3 text-sm" data-testid="payment-success-details">
                <Row label="Plan" value={planName} testId="ps-plan" bold />
                {amount ? <Row label="Billing Amount" value={`${fmtINR(amount)} / month (incl. GST)`} testId="ps-amount" /> : null}
                <Row label="Subscription Status" value={<span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-sm bg-[#16A34A]/10 text-[#16A34A]">{statusLabel}</span>} testId="ps-status" />
                <Row label="Next Billing Date" value={fmtDate(sub?.renewal_date)} testId="ps-renewal" />
                {paymentId ? <Row label="Invoice Reference" value={<span className="font-mono text-xs">{paymentId}</span>} testId="ps-invoice-ref" /> : null}
              </dl>
            )}

            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center justify-center gap-2 bg-[#0B3D91] text-white rounded-none px-5 py-3 font-semibold hover:bg-[#0a347d] transition-colors"
                data-testid="payment-success-dashboard-btn"
              >
                <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
              </button>
              <button
                onClick={() => navigate("/subscription")}
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-300 text-slate-900 rounded-none px-5 py-3 font-semibold hover:border-[#0B3D91] hover:text-[#0B3D91] transition-colors"
                data-testid="payment-success-manage-btn"
              >
                <CreditCard className="h-4 w-4" /> Manage Subscription
              </button>
            </div>

            {paymentId && (
              <button
                onClick={downloadInvoice}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#0B3D91] hover:underline"
                data-testid="payment-success-invoice-btn"
              >
                <Download className="h-4 w-4" /> Download GST Invoice
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          A payment receipt with your GST invoice has been emailed to you.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, testId, bold }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3" data-testid={testId}>
      <dt className="text-slate-500">{label}</dt>
      <dd className={`text-right text-slate-900 ${bold ? "font-bold" : "font-medium"}`}>{value}</dd>
    </div>
  );
}
